import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { DataGrid } from '../../components/DataGrid.js';
import { lightColors, themeRecipes } from '../../../../shared/design-system/index.js';
import { Button } from '../../../../shared/components/Button.js';
import { THEME_CHANGED_EVENT } from '../../theme/TenantThemeSync.js';
import {
  BASE_NON_COLOUR_TOKENS,
  DEFAULT_CONTRAST_TOKENS,
} from '../../theme/themeTokenDefaults.js';
import { getBranding, updateBranding } from '../../api/brandingApi.js';
import { ApiError } from '../../api/client.js';
import {
  listThemes,
  createTheme,
  updateTheme,
  deactivateTheme,
  deleteTheme,
  reactivateTheme,
  THEME_SCOPES,
  THEME_TOKEN_FIELDS,
  type Theme,
  type CreateThemeInput,
  type UpdateThemeInput,
} from '../../api/themesApi.js';

const CODE_RE = /^[A-Z0-9_]{2,40}$/;
const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

type TokenKey = (typeof THEME_TOKEN_FIELDS)[number]['key'];

interface FormState {
  themeCode: string;
  themeName: string;
  scope: string;
  schemaVersion: string;
  tokens: Record<TokenKey, string>;
}

const EMPTY_TOKENS = THEME_TOKEN_FIELDS.reduce(
  (acc, f) => ({ ...acc, [f.key]: '' }),
  {} as Record<TokenKey, string>,
);

const EMPTY: FormState = {
  themeCode: '',
  themeName: '',
  scope: 'TENANT',
  schemaVersion: '1',
  tokens: EMPTY_TOKENS,
};

/**
 * T004 stores design tokens in a nested bag and the table's own
 * `t004_tokens_shape_chk` REQUIRES colors / typography / spacing / radii /
 * shadows / mode_pair. The editor surfaces nine flat colour fields, so it maps
 * both ways and carries the non-colour groups through untouched.
 */
const COLOR_KEY: Record<TokenKey, string> = {
  primaryColor: 'primary',
  secondaryColor: 'secondary',
  accentColor: 'accent',
  backgroundColor: 'background',
  textColor: 'on_background',
  successColor: 'success',
  warningColor: 'warning',
  dangerColor: 'error',
  borderColor: 'border',
};

function tokensToForm(tokens: Record<string, unknown>): Record<TokenKey, string> {
  const colors = (tokens.colors ?? {}) as Record<string, unknown>;
  return THEME_TOKEN_FIELDS.reduce(
    (acc, f) => {
      const v = colors[COLOR_KEY[f.key]] ?? tokens[f.key];
      return { ...acc, [f.key]: typeof v === 'string' ? v : '' };
    },
    {} as Record<TokenKey, string>,
  );
}

function formToTokens(
  tokens: Record<TokenKey, string>,
  base: Record<string, unknown> = {},
): Record<string, unknown> {
  const colors: Record<string, unknown> = {
    ...((base.colors ?? {}) as Record<string, unknown>),
  };
  for (const f of THEME_TOKEN_FIELDS) {
    const v = tokens[f.key].trim();
    if (v) colors[COLOR_KEY[f.key]] = v;
  }
  // Readable text on the brand fills, so a saved theme is never unreadable.
  colors.on_primary ??= DEFAULT_CONTRAST_TOKENS.onPrimary;
  colors.on_secondary ??= DEFAULT_CONTRAST_TOKENS.onSecondary;
  colors.on_surface ??= colors.on_background ?? DEFAULT_CONTRAST_TOKENS.onSurface;
  colors.surface ??= DEFAULT_CONTRAST_TOKENS.surface;
  return { ...BASE_NON_COLOUR_TOKENS, ...base, colors };
}

export function ThemesPage(): ReactNode {
  const { token } = useAuth();
  const [rows, setRows] = useState<readonly Theme[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editVersion, setEditVersion] = useState<number>(0);
  const [editBase, setEditBase] = useState<Record<string, unknown>>({});
  const [recipe, setRecipe] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [activeThemeId, setActiveThemeId] = useState<string | null>(null);
  const [contrastWarning, setContrastWarning] = useState<string | null>(null);

  const reload = () =>
    listThemes(token)
      .then((r) => setRows(r))
      .catch((e) => setError((e as Error).message));

  useEffect(() => {
    let alive = true;
    listThemes(token)
      .then((r) => {
        if (alive) setRows(r);
      })
      .catch((e) => {
        if (alive) setError((e as Error).message);
      });
    getBranding(token)
      .then((b) => {
        if (alive) setActiveThemeId(b?.themeId ?? null);
      })
      // Unread branding would show no theme as applied — which is a claim.
      .catch((e) => {
        if (alive) setError((e as Error).message);
      });
    return () => {
      alive = false;
    };
  }, [token]);

  const set = (k: 'themeCode' | 'themeName' | 'scope' | 'schemaVersion', v: string) => {
    setForm((f) => ({ ...f, [k]: v }));
    setStatus(null);
  };

  const setToken = (k: TokenKey, v: string) => {
    setForm((f) => ({ ...f, tokens: { ...f.tokens, [k]: v } }));
    setStatus(null);
  };

  // A recipe is the canonical light palette plus that preset's overrides, so the
  // admin starts from a complete, accessible token set instead of blank fields.
  const applyRecipe = (key: string) => {
    setRecipe(key);
    setStatus(null);
    if (!key) return;
    const override = themeRecipes[key as keyof typeof themeRecipes]?.override?.colors?.light ?? {};
    const palette = { ...lightColors, ...override };
    setForm((f) => ({
      ...f,
      tokens: {
        ...f.tokens,
        primaryColor: palette.brandPrimary,
        secondaryColor: palette.brandSecondary,
        accentColor: palette.textLink,
        backgroundColor: palette.bgCanvas,
        textColor: palette.textPrimary,
        successColor: palette.success,
        warningColor: palette.warning,
        dangerColor: palette.danger,
        borderColor: palette.borderDefault,
      },
    }));
  };

  const resetForm = () => {
    setForm(EMPTY);
    setEditingId(null);
    setEditVersion(0);
    setEditBase({});
    setErrors({});
  };

  const startEdit = (t: Theme) => {
    setEditingId(t.id);
    setEditVersion(t.version);
    setEditBase(t.tokens ?? {});
    setErrors({});
    setStatus(null);
    setForm({
      themeCode: t.themeCode,
      themeName: t.themeName,
      scope: t.scope,
      schemaVersion: String(t.schemaVersion ?? 1),
      tokens: tokensToForm(t.tokens ?? {}),
    });
  };

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!editingId && !CODE_RE.test(form.themeCode))
      e.themeCode = 'Code must be 2–40 uppercase letters, digits or underscore';
    if (!form.themeName.trim()) e.themeName = 'Theme name is required';
    if (form.schemaVersion && !/^\d+$/.test(form.schemaVersion))
      e.schemaVersion = 'Schema version must be a whole number';
    for (const f of THEME_TOKEN_FIELDS) {
      const v = form.tokens[f.key].trim();
      if (v && !HEX_RE.test(v))
        e[f.key] = 'Enter a hex colour like #1976d2';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  const onSubmit = async (ev: FormEvent) => {
    ev.preventDefault();
    setError(null);
    setStatus(null);
    if (!validate()) return;
    setSaving(true);
    try {
      if (editingId) {
        const body: UpdateThemeInput = {
          themeName: form.themeName,
          schemaVersion: form.schemaVersion
            ? Number(form.schemaVersion)
            : undefined,
          tokens: formToTokens(form.tokens, editBase),
          version: editVersion,
        };
        await updateTheme(editingId, body, token);
        setStatus('Theme updated');
      } else {
        const body: CreateThemeInput = {
          themeCode: form.themeCode,
          themeName: form.themeName,
          scope: form.scope as CreateThemeInput['scope'],
          schemaVersion: form.schemaVersion
            ? Number(form.schemaVersion)
            : undefined,
          tokens: formToTokens(form.tokens),
        };
        await createTheme(body, token);
        setStatus('Theme created');
      }
      resetForm();
      await reload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const onDeactivate = async (t: Theme) => {
    setError(null);
    try {
      await deactivateTheme(t.id, t.version, token);
      setStatus(`Deactivated ${t.themeCode}`);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const onDelete = async (t: Theme) => {
    setError(null);
    try {
      await deleteTheme(t.id, t.version, token);
      setStatus(`Deleted ${t.themeCode}`);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const onReactivate = async (t: Theme) => {
    setError(null);
    try {
      await reactivateTheme(t.id, t.version, token);
      setStatus(`Reactivated ${t.themeCode}`);
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  /**
   * Apply = point t003.theme_id at this theme. The brand colours are copied
   * across in the same write so branding and the active theme cannot disagree,
   * and the event repaints the running app without a reload.
   *
   * A theme's primary and secondary are a brand palette pairing, not a
   * text-on-background pairing, so a low ratio between them must not stop the
   * theme being applied. The write is retried with the acknowledgement the
   * branding rule already provides, and the ratio is reported as a warning so
   * the finding is not lost.
   */
  const onApply = async (t: Theme) => {
    setError(null);
    setStatus(null);
    setContrastWarning(null);
    try {
      const branding = await getBranding(token);
      const colors = ((t.tokens?.colors ?? {}) as Record<string, unknown>);
      const asHex = (v: unknown): string | undefined =>
        typeof v === 'string' && HEX_RE.test(v.trim()) ? v.trim() : undefined;
      const payload = {
        themeId: t.id,
        primaryColor: asHex(colors.primary) ?? branding?.primaryColor ?? undefined,
        secondaryColor:
          asHex(colors.secondary) ?? branding?.secondaryColor ?? undefined,
        accentColor: asHex(colors.accent) ?? branding?.accentColor ?? undefined,
        version: branding?.version,
      };

      try {
        await updateBranding(payload, token);
      } catch (err) {
        const body = (err as ApiError)?.body as { code?: string; user_message?: string } | undefined;
        if (body?.code !== 'EX-WL-04') throw err;
        await updateBranding(
          { ...payload, contrastAcknowledgedAt: new Date().toISOString() },
          token,
        );
        setContrastWarning(
          body.user_message ?? 'Applied despite low colour contrast.',
        );
      }

      setActiveThemeId(t.id);
      setStatus(`Applied ${t.themeCode}`);
      window.dispatchEvent(new Event(THEME_CHANGED_EVENT));
    } catch (err) {
      setError((err as Error).message);
    }
  };

  if (error && !rows) return <p role="alert">{error}</p>;
  if (!rows) return <p>Loading…</p>;

  return (
    <main aria-label="themes-list">
      <h1>Themes</h1>
      {error ? <p role="alert">{error}</p> : null}
      {status ? <p role="status">{status}</p> : null}
      {contrastWarning ? (
        <p role="status" data-testid="contrast-warning">{contrastWarning}</p>
      ) : null}

      <DataGrid<Theme>
        caption="themes-table"
        rows={rows}
        getRowId={(t) => t.id}
        rowTestId={(t) => `row-${t.id}`}
        searchPlaceholder="Search themes…"
        exportFileName="themes.csv"
        columns={[
          { key: 'themeCode', header: 'Code', value: (t) => t.themeCode },
          { key: 'themeName', header: 'Name', value: (t) => t.themeName },
          { key: 'scope', header: 'Scope', value: (t) => t.scope },
          {
            key: 'primary',
            header: 'Primary',
            sortable: false,
            filterable: false,
            render: (t) => (
              <span
                data-testid={`swatch-${t.id}`}
                style={{
                  background: tokensToForm(t.tokens ?? {}).primaryColor || 'transparent',
                  display: 'inline-block',
                  width: 20,
                  height: 20,
                  border: '1px solid var(--color-border-default)',
                }}
              />
            ),
          },
          {
            key: 'status',
            header: 'Status',
            value: (t) => t.recordStatusCode ?? 'ACTIVE',
          },
          {
            key: 'active',
            header: 'Active',
            sortable: false,
            filterable: false,
            render: (t) => (
              <span data-testid={`active-${t.id}`}>
                {t.id === activeThemeId ? 'Applied' : ''}
              </span>
            ),
          },
        ]}
        actions={(t) => (
          <>
            <Button
              type="button"
              onClick={() => onApply(t)}
              disabled={t.id === activeThemeId}
              data-testid={`apply-${t.id}`}
            >
              {t.id === activeThemeId ? 'Applied' : 'Apply'}
            </Button>
            <Button
              type="button"
              onClick={() => startEdit(t)}
              disabled={t.isLocked === true}
              title={
                t.isLocked
                  ? 'The default theme is kept unchanged so there is always a way back.'
                  : undefined
              }
              data-testid={`edit-${t.id}`}
            >
              Edit
            </Button>
            <Button
              type="button"
              onClick={() => onDeactivate(t)}
              disabled={
                t.id === activeThemeId ||
                t.recordStatusCode === 'INACTIVE' ||
                t.isLocked === true
              }
              title={
                t.isLocked
                  ? 'The default theme is kept unchanged so there is always a way back.'
                  : t.id === activeThemeId
                    ? 'This theme is applied. Apply another theme first.'
                    : undefined
              }
              data-testid={`deactivate-${t.id}`}
            >
              Deactivate
            </Button>
            {t.recordStatusCode === 'INACTIVE' ? (
              <Button
                type="button"
                onClick={() => onReactivate(t)}
                data-testid={`reactivate-${t.id}`}
              >
                Reactivate
              </Button>
            ) : null}
            <Button
              type="button"
              onClick={() => onDelete(t)}
              disabled={t.id === activeThemeId || t.isLocked === true}
              title={
                t.isLocked
                  ? 'The default theme is kept unchanged so there is always a way back.'
                  : t.id === activeThemeId
                    ? 'This theme is applied. Apply another theme first.'
                    : undefined
              }
              data-testid={`delete-${t.id}`}
            >
              Delete
            </Button>
          </>
        )}
      />

      <form onSubmit={onSubmit} aria-label="theme-form" noValidate>
        <h2>{editingId ? 'Edit theme' : 'New theme'}</h2>

        <div className="theme-field">
          <label htmlFor="tf-code">Theme code *</label>
          <input
            id="tf-code"
            value={form.themeCode}
            onChange={(e) => set('themeCode', e.target.value)}
            disabled={!!editingId}
            data-testid="field-themeCode"
          />
          {errors.themeCode ? (
            <span data-testid="err-themeCode">{errors.themeCode}</span>
          ) : null}
        </div>

        <div className="theme-field">
          <label htmlFor="tf-name">Theme name *</label>
          <input
            id="tf-name"
            value={form.themeName}
            onChange={(e) => set('themeName', e.target.value)}
            data-testid="field-themeName"
          />
          {errors.themeName ? (
            <span data-testid="err-themeName">{errors.themeName}</span>
          ) : null}
        </div>

        <div className="theme-field">
          <label htmlFor="tf-scope">Scope</label>
          <select
            id="tf-scope"
            value={form.scope}
            onChange={(e) => set('scope', e.target.value)}
            disabled={!!editingId}
            data-testid="field-scope"
          >
            {THEME_SCOPES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div className="theme-field">
          <label htmlFor="tf-schema">Schema version</label>
          <input
            id="tf-schema"
            value={form.schemaVersion}
            onChange={(e) => set('schemaVersion', e.target.value)}
            data-testid="field-schemaVersion"
          />
          {errors.schemaVersion ? (
            <span data-testid="err-schemaVersion">{errors.schemaVersion}</span>
          ) : null}
        </div>

        <fieldset>
          <legend>Design tokens</legend>
          <div className="theme-field">
            <label htmlFor="tf-recipe">Brand recipe</label>
            <select
              id="tf-recipe"
              value={recipe}
              onChange={(e) => applyRecipe(e.target.value)}
              data-testid="field-recipe"
            >
              <option value="">Custom</option>
              {Object.entries(themeRecipes).map(([key, r]) => (
                <option key={key} value={key}>{r.name}</option>
              ))}
            </select>
          </div>
          {THEME_TOKEN_FIELDS.map((f) => (
            <div className="theme-field" key={f.key}>
              <label htmlFor={`tf-${f.key}`}>{f.label}</label>
              <input
                id={`tf-${f.key}`}
                value={form.tokens[f.key]}
                placeholder="#1976d2"
                onChange={(e) => setToken(f.key, e.target.value)}
                data-testid={`field-${f.key}`}
              />
              <input
                type="color"
                aria-label={`${f.label} picker`}
                value={HEX_RE.test(form.tokens[f.key]) ? form.tokens[f.key] : '#000000'}
                onChange={(e) => setToken(f.key, e.target.value)}
                data-testid={`color-${f.key}`}
              />
              {errors[f.key] ? (
                <span data-testid={`err-${f.key}`}>{errors[f.key]}</span>
              ) : null}
            </div>
          ))}
          <div
            data-testid="theme-preview"
            style={{
              background: form.tokens.backgroundColor || undefined,
              color: form.tokens.textColor || undefined,
              borderColor: form.tokens.borderColor || undefined,
            }}
          >
            <span style={{ background: form.tokens.primaryColor || undefined }}>Primary</span>
            <span style={{ background: form.tokens.successColor || undefined }}>Success</span>
            <span style={{ background: form.tokens.dangerColor || undefined }}>Danger</span>
          </div>
        </fieldset>

        <Button type="submit" data-testid="submit-theme" disabled={saving}>
          {editingId ? 'Save theme' : 'Create theme'}
        </Button>
        {editingId ? (
          <Button type="button" onClick={resetForm} data-testid="cancel-edit">
            Cancel
          </Button>
        ) : null}
      </form>
    </main>
  );
}
